import { useState } from 'react';
import { Clock3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MILK_SESSIONS, sessionsSchema, type MilkSession } from '@dairy/shared';
import { useSettings } from '@/auth/useSession';
import { Badge, Button, Card, Checkbox, Input, PageHeader } from '@/components/ui';
import { useSaveTenant } from './useTenantSettings';

const DEFAULT_TIMES: Record<MilkSession, string> = { MORNING: '06:00', MIDDAY: '12:00', EVENING: '17:00', SESSION_4: '19:00', SESSION_5: '21:00', SESSION_6: '23:00' };

/** MR-2 / S2-T08: enable 1–3 sessions in day order with times; changing sessions never changes past records. */
export function SessionsSettingsPage() {
  const { t } = useTranslation();
  const s = useSettings();
  const { saveTenant, saving } = useSaveTenant();
  const [state, setState] = useState(() =>
    Object.fromEntries(
      MILK_SESSIONS.map((m) => {
        const cur = s.sessions.find((x) => x.session === m);
        return [m, { enabled: !!cur, time: cur?.time ?? DEFAULT_TIMES[m], name: cur?.name ?? '' }];
      }),
    ) as Record<MilkSession, { enabled: boolean; time: string; name: string }>,
  );
  const [error, setError] = useState<string | null>(null);
  const activeCount = MILK_SESSIONS.filter((session) => state[session].enabled).length;
  const submit = async () => {
    const sessions = MILK_SESSIONS.filter((m) => state[m].enabled).map((m) => ({ session: m, time: state[m].time, name: state[m].name.trim() })).sort((a, b) => a.time.localeCompare(b.time));
    const r = sessionsSchema.safeParse(sessions);
    if (!r.success) return setError(t('settings.sessionsInvalid'));
    setError(null);
    await saveTenant({ settings: { sessions: r.data } });
  };
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title={t('settings.sessions')} />
      <Card className="space-y-4">
        <div className="flex items-center justify-between gap-3"><h2 className="text-lg font-semibold">{t('settings.sessionSchedule')}</h2><Badge tone="green">{t('settings.sessionsEnabled', { count: activeCount })}</Badge></div>
        {MILK_SESSIONS.map((m) => (
          <fieldset key={m} className={`rounded-xl border p-4 ${state[m].enabled ? 'border-brand-200 bg-brand-50/40' : 'border-stone-200 bg-stone-50'}`}>
            <legend className="sr-only">{t(`milk.session.${m}`)}</legend>
            <Checkbox label={t(`milk.session.${m}`)} checked={state[m].enabled} onChange={(e) => { setError(null); setState({ ...state, [m]: { ...state[m], enabled: e.target.checked } }); }} />
            {state[m].enabled ? <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_10rem]">
              <label className="space-y-1 text-sm font-medium text-stone-700"><span>{t('settings.sessionName')}</span><Input value={state[m].name} maxLength={40} onChange={(e) => setState({ ...state, [m]: { ...state[m], name: e.target.value } })} placeholder={t(`milk.session.${m}`)} /></label>
              <label className="space-y-1 text-sm font-medium text-stone-700"><span className="flex items-center gap-1"><Clock3 aria-hidden="true" className="h-4 w-4" />{t('settings.sessionTime')}</span><Input type="time" value={state[m].time} onChange={(e) => { setError(null); setState({ ...state, [m]: { ...state[m], time: e.target.value } }); }} /></label>
            </div> : null}
          </fieldset>
        ))}
        {error ? <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-800">{error}</p> : null}
        <Button className="w-full sm:w-auto" disabled={saving} onClick={() => void submit()}>
          {t('common.save')}
        </Button>
      </Card>
    </div>
  );
}
